/**
 * calendarModel — date logic of the study calendar (no React).
 *
 * Days are keyed by civil date 'YYYY-MM-DD' (same key the server stores).
 * Month grids exist in two flavours: a Hebrew month (default — the plan is Hebrew-calendar based)
 * and a Gregorian month; both are built as whole weeks Sunday → Shabbat.
 */
import { HDate, gematriya } from '@hebcal/hdate';
import { GEMARA_TRACTATES } from '../../data/gemaraTractates';

export const TRACKS = [
  { key: 'dafYomi', frame: 'daf_yomi', label: 'דף היומי', short: 'ד״י', tone: 'primary', icon: 'book' },
  { key: 'shinun', frame: 'shinun', label: 'דף השינון', short: 'שינון', tone: 'success', icon: 'repeat' },
  { key: 'meshannim', frame: 'meshannim', label: 'משננים', short: 'משננים', tone: 'violet', icon: 'users' },
];
export const TRACK_BY_FRAME = Object.fromEntries(TRACKS.map((t) => [t.frame, t]));

export const WEEKDAYS = ['ראשון', 'שני', 'שלישי', 'רביעי', 'חמישי', 'שישי', 'שבת'];
export const WEEKDAY_LETTERS = ['א׳', 'ב׳', 'ג׳', 'ד׳', 'ה׳', 'ו׳', 'ש׳'];
const GREG_MONTHS = ['ינואר', 'פברואר', 'מרץ', 'אפריל', 'מאי', 'יוני', 'יולי', 'אוגוסט', 'ספטמבר', 'אוקטובר', 'נובמבר', 'דצמבר'];
const GREG_MONTHS_SHORT = ['ינו׳', 'פבר׳', 'מרץ', 'אפר׳', 'מאי', 'יוני', 'יולי', 'אוג׳', 'ספט׳', 'אוק׳', 'נוב׳', 'דצמ׳'];

/* ── keys ── */
const pad2 = (n) => String(n).padStart(2, '0');
export const dateKey = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
export function fromKey(key) {
  const [y, m, d] = String(key).split('-').map(Number);
  return new Date(y, m - 1, d);
}
export const addDays = (d, n) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
export const daysBetween = (a, b) => Math.round((fromKey(b) - fromKey(a)) / 86400000);

/** today in Israel (the plan's civil day) */
export function israelTodayKey() {
  try {
    return new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Jerusalem', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());
  } catch {
    return dateKey(new Date());
  }
}

/* ── Hebrew dates ── */
export function hebrewMonthName(month, year) {
  const leap = HDate.isLeapYear(year);
  switch (month) {
    case 1: return 'ניסן';
    case 2: return 'אייר';
    case 3: return 'סיון';
    case 4: return 'תמוז';
    case 5: return 'אב';
    case 6: return 'אלול';
    case 7: return 'תשרי';
    case 8: return 'חשון';
    case 9: return 'כסלו';
    case 10: return 'טבת';
    case 11: return 'שבט';
    case 12: return leap ? 'אדר א׳' : 'אדר';
    case 13: return 'אדר ב׳';
    default: return '';
  }
}

/** { day, month, year, dayGem, monthName, yearGem, label:'כ״ב בחשון תשפ״ז' } */
export function hebrewOf(date) {
  const h = new HDate(date);
  const day = h.getDate();
  const month = h.getMonth();
  const year = h.getFullYear();
  const monthName = hebrewMonthName(month, year);
  const dayGem = gematriya(day);
  const yearGem = gematriya(year);
  return { day, month, year, dayGem, monthName, yearGem, label: `${dayGem} ב${monthName} ${yearGem}` };
}

export function gregLabel(date, { short = false, year = true } = {}) {
  const m = (short ? GREG_MONTHS_SHORT : GREG_MONTHS)[date.getMonth()];
  return `${date.getDate()} ב${m}${year ? ` ${date.getFullYear()}` : ''}`;
}
export const gregMonthShort = (date) => GREG_MONTHS_SHORT[date.getMonth()];

/* ── month cursors ── */
/** cursor: { mode:'hebrew', year, month } | { mode:'greg', year, month(1-12) } */
export function cursorFor(mode, date) {
  if (mode === 'greg') return { mode, year: date.getFullYear(), month: date.getMonth() + 1 };
  const h = new HDate(date);
  return { mode: 'hebrew', year: h.getFullYear(), month: h.getMonth() };
}

/** [first, last] civil dates of the cursor's month */
export function monthBounds(cursor) {
  if (cursor.mode === 'greg') {
    return [new Date(cursor.year, cursor.month - 1, 1), new Date(cursor.year, cursor.month, 0)];
  }
  const first = new HDate(1, cursor.month, cursor.year);
  const len = HDate.daysInMonth(cursor.month, cursor.year);
  return [first.greg(), new HDate(first.abs() + len - 1).greg()];
}

export function shiftCursor(cursor, delta) {
  if (cursor.mode === 'greg') {
    const d = new Date(cursor.year, cursor.month - 1 + delta, 1);
    return { mode: 'greg', year: d.getFullYear(), month: d.getMonth() + 1 };
  }
  const [first, last] = monthBounds(cursor);
  const target = delta > 0 ? addDays(last, 1) : addDays(first, -1);
  return cursorFor('hebrew', target);
}

/** title + subtitle of the visible month (each calendar names the other one's span) */
export function monthTitle(cursor) {
  const [first, last] = monthBounds(cursor);
  if (cursor.mode === 'greg') {
    const a = hebrewOf(first);
    const b = hebrewOf(last);
    const sub = a.month === b.month ? `${a.monthName} ${a.yearGem}`
      : a.year === b.year ? `${a.monthName} – ${b.monthName} ${b.yearGem}` : `${a.monthName} ${a.yearGem} – ${b.monthName} ${b.yearGem}`;
    return { title: `${GREG_MONTHS[cursor.month - 1]} ${cursor.year}`, sub };
  }
  const sameYear = first.getFullYear() === last.getFullYear();
  const sub = first.getMonth() === last.getMonth() && sameYear
    ? `${GREG_MONTHS[first.getMonth()]} ${first.getFullYear()}`
    : `${GREG_MONTHS[first.getMonth()]}${sameYear ? '' : ` ${first.getFullYear()}`} – ${GREG_MONTHS[last.getMonth()]} ${last.getFullYear()}`;
  return { title: `${hebrewMonthName(cursor.month, cursor.year)} ${gematriya(cursor.year)}`, sub };
}

/** weeks of the month: [[{ key, date, inMonth }×7], …] */
export function monthWeeks(cursor) {
  const [first, last] = monthBounds(cursor);
  const start = addDays(first, -first.getDay());
  const end = addDays(last, 6 - last.getDay());
  const weeks = [];
  for (let d = start; d <= end; d = addDays(d, 7)) {
    weeks.push(Array.from({ length: 7 }, (_, i) => {
      const date = addDays(d, i);
      return { key: dateKey(date), date, inMonth: date >= first && date <= last };
    }));
  }
  return weeks;
}

/* ── day classification ── */
const YOMTOV_RE = /ראש השנה|יום הכפורים|חג הסוכות|שמיני עצרת|שמחת תורה|א' דפסח|שביעי של פסח|חג השבועות/;
const FAST_RE = /צום|תענית אסתר|תשעה באב/;
const FESTIVE_RE = /חנוכה|פורים|חוהמ|חול המועד|הושענא|ראש חו?דש|ל"ג בעומר|ט"ו בשבט|ראש השנה לאילנות|חמשה עשר באב|אסרו חג|ערב/;

/** 'yomtov' | 'fast' | 'festive' | '' */
export function eventKind(text) {
  const t = String(text || '');
  if (!t) return '';
  if (/^ערב/.test(t)) return 'festive';
  if (YOMTOV_RE.test(t)) return 'yomtov';
  if (FAST_RE.test(t)) return 'fast';
  if (FESTIVE_RE.test(t)) return 'festive';
  return 'note';
}

/**
 * Fallback events for days the plan does not cover (other years / systems without a plan):
 * Rosh Chodesh and the fixed-date festivals. Fasts that move with Shabbat are left to the plan.
 */
export function fallbackEvent(date) {
  const { day, month, year } = hebrewOf(date);
  const last = HDate.monthsInYear(year);
  const adar = HDate.isLeapYear(year) ? 13 : 12;
  const fixed = {
    7: { 1: "א' דראש השנה", 2: "ב' דראש השנה", 10: 'יום הכפורים', 15: 'חג הסוכות', 21: 'הושענא רבה', 22: 'שמיני עצרת ושמחת תורה' },
    1: { 14: 'ערב פסח', 15: "א' דפסח", 21: 'שביעי של פסח' },
    3: { 6: 'חג השבועות' },
    11: { 15: 'ט"ו בשבט' },
    [adar]: { 14: 'פורים', 15: 'שושן פורים' },
    2: { 18: 'ל"ג בעומר' },
  };
  if (fixed[month]?.[day]) return fixed[month][day];
  if (month === 7 && day >= 16 && day <= 20) return 'חול המועד סוכות';
  if (month === 1 && day >= 16 && day <= 20) return 'חול המועד פסח';
  const hanukkahStart = new HDate(25, 9, year).abs();
  const abs = new HDate(date).abs();
  if (abs >= hanukkahStart && abs < hanukkahStart + 8) return `${gematriya(abs - hanukkahStart + 1)} דחנוכה`;
  if (day === 30) return `ראש חודש ${hebrewMonthName(month === last ? 1 : month + 1, year)}`;
  if (day === 1 && month !== 7) return `ראש חודש ${hebrewMonthName(month, year)}`;
  return '';
}

/* ── study text helpers ── */
const AMUD = { a: '.', b: ':', '': '' };
const pageText = (p, withTractate) => (p ? `${withTractate ? `${p.tractate} ` : ''}${gematriya(p.daf)}${AMUD[p.amud || '']}` : '');

/** compact shinun text: "פסחים ד. – ד:" (one amud → "פסחים ב.") */
export function shinunText(s) {
  if (!s) return '';
  if (s.review) return 'שינון וחזרה';
  if (!s.from || !s.to) return s.text;
  if (s.from.tractate === s.to.tractate && s.from.daf === s.to.daf && s.from.amud === s.to.amud) return pageText(s.from, true);
  if (s.from.tractate === s.to.tractate) return `${pageText(s.from, true)} – ${pageText(s.to, false)}`;
  return `${pageText(s.from, true)} – ${pageText(s.to, true)}`;
}

/** the text of one track on a day ('' when the track has nothing that day) */
export function trackText(day, key) {
  const v = day?.[key];
  if (!v) return '';
  if (key === 'shinun') return shinunText(v);
  return v.text || '';
}
export const isReview = (day, key) => !!day?.[key]?.review;

/** tractate progress of a daf: { tractate, daf, max, pct } */
export function tractateProgress(tractate, daf) {
  const max = GEMARA_TRACTATES[tractate];
  if (!max || !daf) return null;
  return { tractate, daf, max, pct: Math.max(0, Math.min(100, Math.round(((daf - 1) / (max - 1)) * 100))) };
}

/** which tractate a track is in on a day (for the year timeline) */
export function trackTractate(day, key) {
  const v = day?.[key];
  if (!v) return '';
  if (key === 'shinun') return v.to?.tractate || v.from?.tractate || '';
  return v.tractate || '';
}

/**
 * yearSegments — consecutive runs of the same tractate per track, over the whole plan.
 * Review days inherit the tractate around them so a run is not cut by "שינון וחזרה".
 * → { [trackKey]: [{ tractate, from, to, days }] }
 */
export function yearSegments(days) {
  const out = {};
  TRACKS.forEach(({ key }) => {
    const segs = [];
    let cur = null;
    days.forEach((day) => {
      const t = trackTractate(day, key) || (day[key]?.review && cur ? cur.tractate : '');
      if (!t) { cur = null; return; }
      if (cur && cur.tractate === t && daysBetween(cur.to, day.date) <= 3) {
        cur.to = day.date;
      } else {
        cur = { tractate: t, from: day.date, to: day.date };
        segs.push(cur);
      }
    });
    out[key] = segs.map((s) => ({ ...s, days: daysBetween(s.from, s.to) + 1 }));
  });
  return out;
}

/** upcoming exams from a date (inclusive): [{ date, track, text, inDays }] */
export function upcomingExams(days, fromKeyValue, limit = 6) {
  const out = [];
  for (const day of days) {
    if (day.date < fromKeyValue || !day.exams?.length) continue;
    day.exams.forEach((e) => out.push({ date: day.date, track: e.track, text: e.text, inDays: daysBetween(fromKeyValue, day.date) }));
    if (out.length >= limit) break;
  }
  return out.slice(0, limit);
}

/** the parasha of the week that contains the date (read on that week's Shabbat) */
export function weekParasha(byDate, key) {
  const d = fromKey(key);
  const shabbat = dateKey(addDays(d, 6 - d.getDay()));
  return byDate.get(shabbat)?.parasha || '';
}

/** "בעוד 3 ימים" / "היום" / "מחר" */
export function inDaysLabel(n) {
  if (n === 0) return 'היום';
  if (n === 1) return 'מחר';
  if (n === 2) return 'מחרתיים';
  return `בעוד ${n} ימים`;
}
